import {React, styled} from 'common'
import {Texts, Colors} from 'appearance'
import Button from 'components/Control/Button'
import FormTemplate from 'components/Control/FormTemplate'
import {ReactComponent as Logo} from 'assets/images/logo-no-background.svg'
const LoginBasic = ({useAction, i18nCategory, children}) => {
    const {handleSubmit, onSubmit, error, mediumScreen, methods, t, formField} = useAction({
        i18nCategory,
    })

    return (
        <Container>
            {!mediumScreen && (
                <LeftSide>
                    <HeroContent>
                        <Logo
                            style={{height: 30, width: 200}}
                            fill={Colors.white}
                            stroke={Colors.white}
                        />
                        <HeroTitle>
                            {t(
                                `login.hero_title`,
                                'Powering efficient energy management for modern utilities.'
                            )}
                        </HeroTitle>
                        <HeroText>
                            {t(
                                `login.subtitle`,
                                'Monitor, analyse, and optimise energy consumption with confidence.'
                            )}
                        </HeroText>
                        <HighlightList>
                            <HighlightRow>
                                <AccentDot />
                                <HeroFootnote>
                                    {t(
                                        `login.highlight_1`,
                                        'Real-time dashboards and advanced analytics'
                                    )}
                                </HeroFootnote>
                            </HighlightRow>
                            <HighlightRow>
                                <AccentDot />
                                <HeroFootnote>
                                    {t(
                                        `login.highlight_2`,
                                        'Secure access tailored for your organisation'
                                    )}
                                </HeroFootnote>
                            </HighlightRow>
                        </HighlightList>
                    </HeroContent>
                    <HeroOverlay />
                </LeftSide>
            )}
            <RightSide>
                <LoginSection>
                    <FormHeader>
                        <FormTitle>{t(`${i18nCategory}.title`)}</FormTitle>
                        <FormSubtitle>{t(`${i18nCategory}.welcome`)}</FormSubtitle>
                    </FormHeader>
                    <FormTemplate fields={formField} methods={methods} i18n={i18nCategory} />
                    <ButtonStyled onClick={handleSubmit(onSubmit)} variant="contained">
                        {t(`${i18nCategory}.submit`)}
                    </ButtonStyled>
                    {children}
                    {error && <ErrorText>{t(`${i18nCategory}.error`)}</ErrorText>}
                </LoginSection>
            </RightSide>
        </Container>
    )
}

export default LoginBasic
const Container = styled.div`
    width: 100vw;
    height: 100dvh;
    display: flex;
    align-items: stretch;
    background: linear-gradient(135deg, #0f172a, #111c30);
`

const LeftSide = styled.div`
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 96px 80px;
    overflow: hidden;
    background: linear-gradient(150deg, rgba(33, 150, 243, 0.92) 0%, rgba(16, 24, 40, 0.95) 65%);
    color: ${Colors.white};
`

const RightSide = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 64px 48px;
    background: ${Colors.background};
    @media (max-width: 1024px) {
        padding: 48px 32px;
    }
`

const HeroContent = styled.div`
    position: relative;
    z-index: 1;
    max-width: 460px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`

const HeroTitle = styled(Texts.Title)`
    color: ${Colors.white};
    font-size: 38px;
    line-height: 1.25;
    margin: 0;
`

const HeroText = styled(Texts.RegularText)`
    color: ${Colors.white};
    opacity: 0.85;
    font-size: 16px;
    line-height: 1.7;
`

const HighlightList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
`

const HighlightRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const AccentDot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${Colors.white};
    opacity: 0.6;
`

const HeroFootnote = styled(Texts.RegularText)`
    color: ${Colors.white};
    opacity: 0.78;
    font-size: 14px;
`

const HeroOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% -10%, rgba(255, 255, 255, 0.18), transparent 55%),
        radial-gradient(circle at 85% 30%, rgba(255, 255, 255, 0.08), transparent 60%);
    pointer-events: none;
`

const LoginSection = styled.div`
    width: 100%;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 48px 40px;
    border-radius: 28px;
    @media (max-width: 640px) {
        padding: 36px 28px;
        border-radius: 22px;
    }
`

const FormHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const FormTitle = styled(Texts.Title)`
    font-size: 28px;
    margin: 0;
    color: ${Colors.primary};
`

const FormSubtitle = styled(Texts.RegularText)`
    font-size: 14px;
`

const ButtonStyled = styled(Button)`
    width: 100%;
    margin-top: 4px;
    padding-block: 12px;
    font-weight: 600;
    box-shadow: 0 12px 24px -12px rgba(94, 122, 255, 0.45);
`

const ErrorText = styled(Texts.RegularText)`
    color: ${Colors.red};
    text-align: center;
    font-size: 12px;
`
